import { useEffect, useMemo, useState } from "react";
import {
  CarIcon,
  DeleteIcon,
  EditIcon,
  PrintIcon,
  ProfileIcon,
  ReceiptIcon
} from "../../utils/icon.utils";
import Table from "../../ui/Table/Table";
import { GroupBtn } from "../../ui/GroupBtn";
import { extractValues, filterValues } from "../../utils/stockList.utils";
import {
  useCustomerAttachmentPrint,
  useDeleteCustomerAttachmentMutation,
  useGetAllCustomerAttachmentQuery
} from "../../utils/customerAttachment.utils";
import { onConfirm } from "../../utils/sweetAlert.utils";
import Loading from "../../components/Loading";
import PageLayout from "../../layouts/PageLayout";
import { useNavigate } from "react-router-dom";
import { objectToQueryString } from "../../utils/object.utils";
import QuotationToolBar from "./QuotationToolBar";
import { printDate, tableDate } from "../../utils/date.utils";
import { getJson } from "../../utils/json.utils";
import { arrayMap, arrayTextPrint } from "../../utils/array.utils";
import { currency } from "../../utils/number.utils";
import { Badge } from "reactstrap";
import Button from "../../ui/Button";
import { useQueryParams } from "../../utils/queryParams.utils";
import PrintDateModal from "../MoneyReceiptView/PrintDateModal";
import { attachmentParsedForMoneyReceipt } from "../../utils/moneyReceipt.utils";

export default function QuotationListPage({
  isBreadCrumbs = true,
  style = { hasCardBg: true, pageLayoutClassName: "pt-4" },
  customer: existCustomer,
  isCustomer = false
}) {
  const {
    fullPath: redirectTo,
    searchParams,
    removeParamsFromObject,
    location
  } = useQueryParams();

  const { cleanedUrl } = removeParamsFromObject(["tab"]);

  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearched, setIsSearched] = useState(false);
  const pageSize = 10;

  // print quotation
  const [printModal, setPrintModal] = useState(false);
  const [printCaid, setPrintCaid] = useState("");
  const printToggle = () => {
    setPrintModal((prev) => !prev);
  };

  // print money receipt
  const { printParams, setPrintParams, printCount, setPrintCount, printLoading } =
    useCustomerAttachmentPrint();

  const onMoneyReceipt = (data) => {
    const qoutToMr = attachmentParsedForMoneyReceipt(data);
    navigate("/money-receipt-list/add", {
      state: { data: { qoutToMr }, redirectTo: `${location.pathname}?tab=2&${cleanedUrl}` }
    });
  };

  const onPrint = (data) => {
    const id = data?.caid ? data.caid : "";
    setPrintCaid(id);
    printToggle();
  };

  const handlePrint = (submittedData) => {
    const { print_pad_type, bank_format } = submittedData;

    setPrintParams({
      caid: printCaid,
      time: printCount,
      print_pad_type,
      bank_format
    });
    setPrintCount((prev) => prev + 1);
  };

  // is exist customer information?
  const isExistCustomer = !!existCustomer?.profile?.id;

  // filter data
  const { setFilter, getFilter } = filterValues();
  const filteredValues = getFilter();
  const { values } = extractValues(filteredValues);

  const initParams = useMemo(() => {
    return {
      page: 1,
      size: pageSize,
      type_id: 1,
      // initiall set filter data if have,
      customer_id: existCustomer?.profile?.id ?? "",
      ...values
    };
  }, [existCustomer?.profile?.id, values]);

  const [params, setParams] = useState(initParams);

  useEffect(() => {
    if (existCustomer?.profile?.id) {
      setParams((prev) => ({ ...prev, customer_id: existCustomer?.profile?.id }));
    }
  }, [existCustomer?.profile?.id]);

  useEffect(() => {
    setParams((prev) => ({ ...prev, page: currentPage }));
  }, [currentPage]);

  const { isLoading: loading, data } = useGetAllCustomerAttachmentQuery({
    query: `?${objectToQueryString(params)}&${searchParams}`,
    ...(isCustomer
      ? {
          options: {
            enabled: !!params?.customer_id
          }
        }
      : {})
  });

  const onSearch = (values) => {
    setFilter(null);
    setParams({ ...initParams, page: 1, size: pageSize, ...values });
    setIsSearched(true);
  };

  const handleSearchClear = () => {
    setParams({ ...initParams, page: 1, size: pageSize });
    setIsSearched(false);
  };

  const onFilterClear = () => {
    setFilter(null);
    setParams({ ...initParams, page: 1, size: pageSize });
  };

  const { loading: deleting, deleteCustomerAttachment } = useDeleteCustomerAttachmentMutation([
    "deleteCustomerAttachment"
  ]);

  const totalPage = data?.last_page === 1 && data?.total === 0 ? 0 : data?.last_page;

  const breadCrumbs = isBreadCrumbs
    ? [
        {
          name: "Pages",
          link: "/pages"
        },
        {
          name: "Quotation List"
        }
      ]
    : [];

  const customerThead = !isExistCustomer ? ["Customer"] : [];

  const thead = ["#", "Car", ...customerThead, "Quot Date", "Date", "Action"];

  function parseAttachement(attachment) {
    return attachment ? getJson(attachment) : {};
  }

  const QuotDate = ({ data }) => {
    const attachment = parseAttachement(data?.attachment);
    const quotDate = attachment?.qoutDate ? printDate(attachment.qoutDate) : "-";
    const validUntil = attachment?.validUntil ? printDate(attachment.validUntil) : "-";
    return (
      <div>
        Date: {quotDate} <br /> Valid Until: {validUntil}
      </div>
    );
  };

  const CustomerInfo = ({ data }) => {
    const attachment = parseAttachement(data?.attachment);

    let customer = attachment?.customer ?? {};
    delete customer?.customer_id;

    const customerArray = Object.values(customer);

    const customer_id = data?.customer?.customer_id;
    return (
      <div>
        {arrayTextPrint(customerArray)}
        <br />
        {!!customer_id && (
          <Button onClick={() => navigate(`/customer/${customer_id}/view`)} icon={ProfileIcon} />
        )}
      </div>
    );
  };

  const CarInfo = ({ data }) => {
    const attachment = parseAttachement(data?.attachment);
    const car = attachment?.car ?? {};
    const carArray = [car?.chassis ?? "", car?.name ?? "", car?.year ?? "", car?.color ?? ""];

    if (data?.is_multi) {
      return (
        <div>
          <Badge color="info">{car?.length}</Badge> Multi Cars
          <br /> {!isNaN(attachment?.amount?.total) ? currency(attachment.amount.total) : ""}
        </div>
      );
    }

    const carId = car?.sku ?? "";
    return (
      <div>
        {arrayTextPrint(carArray)}
        <br /> {car?.total ? currency(car.total) : ""}
        <br />
        {!!carId && (
          <Button
            color="info"
            onClick={() => navigate(`/stock-list/${carId}/view`)}
            icon={CarIcon}
          />
        )}
      </div>
    );
  };

  const tbody =
    arrayMap(data?.data, (item) => {
      const date = item?.created_at ? tableDate(item.created_at) : "-";
      return {
        "#": item?.caid ?? "-",
        Customer: <CustomerInfo data={item} />,
        Car: <CarInfo data={item} />,
        "Quot Date": <QuotDate data={item} />,
        Date: date,
        ...item
      };
    }) || [];

  const ActionComponent = ({ data }) => {
    const handleEdit = (item) => {
      navigate(`/quotation-list/${item?.caid}/edit`, {
        state: { data: existCustomer, redirectTo }
      });
    };

    const handleDelete = async (item) => {
      const { isConfirmed } = await onConfirm();
      if (isConfirmed) {
        await deleteCustomerAttachment(item?.id);
      }
    };

    const btns = [
      {
        icon: DeleteIcon,
        onClick: () => handleDelete(data),
        color: "danger"
      },
      {
        icon: EditIcon,
        onClick: () => handleEdit(data),
        color: "primary"
      },
      {
        icon: PrintIcon,
        onClick: () => onPrint(data),
        color: "info"
      },
      {
        icon: ReceiptIcon,
        onClick: () => onMoneyReceipt(data),
        color: "success"
      }
    ];
    return <GroupBtn btns={btns} justify="start" loading={printLoading} />;
  };

  const isLoading = loading || deleting || printLoading;

  const handleAdd = () => {
    navigate("/quotation-list/add", { state: { data: existCustomer, redirectTo } });
  };

  return (
    <PageLayout
      title="Quotation List"
      subTitle={`Total: ${data?.total ?? 0}`}
      breadCrumbs={breadCrumbs}
      style={style}>
      <QuotationToolBar
        handleAdd={handleAdd}
        filteredValues={filteredValues}
        onFilterClear={onFilterClear}
        onSearch={onSearch}
        isSearched={isSearched}
        handleSearchClear={handleSearchClear}
      />

      <Table
        thead={thead}
        tbody={tbody}
        action={ActionComponent}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPage={totalPage}
      />

      {printModal && (
        <PrintDateModal
          modal={printModal}
          toggle={printToggle}
          printDate={printParams.printDate}
          handlePrint={handlePrint}
          modalTitle={"Quotation"}
          modalType={["quotation"]}
        />
      )}

      {<Loading loading={isLoading} />}
    </PageLayout>
  );
}
